/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Matrix4, Vector3, Quaternion, Euler } from 'three';
/**
 * helper to set the object orientation
 * the position / rotation / scaling etc.
 */
export class Orientation {
    /**
     * sets the orientation of a CapDataDesc to an object3d
     * @param object3D
     * @param inObject the orientation inside will be used to set
     */
    static set(object3D, inObject) {
        if (inObject != null && inObject.orientation != null) {
            if (Array.isArray(inObject.orientation)) {
                Orientation.setOrientationArray(object3D, inObject.orientation);
            }
            else {
                Orientation.setOrientation(object3D, inObject.orientation);
            }
        }
    }
    /**
     * prepends a new orientation to the orientation list inside the CapDataDesc
     * if the orientation is not a list it will be transformed to one
     * @param inObject
     * @param newData
     */
    static prepend(inObject, newData) {
        if (!inObject.orientation) {
            inObject.orientation = [newData];
        }
        else if (Array.isArray(inObject.orientation)) {
            inObject.orientation = [newData, ...inObject.orientation];
        }
        else {
            inObject.orientation = [newData, inObject.orientation];
        }
    }
    /**
     * appends a new orientation to the orientation list inside the CapDataDesc
     * if the orientation is not a list it will be transformed to one
     * @param inObject
     * @param newData
     */
    static append(inObject, newData) {
        if (!inObject.orientation) {
            inObject.orientation = [newData];
        }
        else if (Array.isArray(inObject.orientation)) {
            inObject.orientation = [...inObject.orientation, newData];
        }
        else {
            inObject.orientation = [inObject.orientation, newData];
        }
    }
    /**
     * applies all orientations to the object
     * @param object3D
     * @param orientationArray
     */
    static setOrientationArray(object3D, orientationArray) {
        const mat4 = new Matrix4();
        Orientation.fromOrientationArrayToMatrix(orientationArray, mat4);
        this.setMatrix(object3D, mat4);
    }
    /**
     * multiplies the Orientation Array to one single Matrix
     * in such a way that the order of operation is fullfilled
     * e.g. [A, B, C] A is applied first than B, than C => the produkt is C*B*A
     * @param orientationArray
     * @param mat4
     */
    static fromOrientationArrayToMatrix(orientationArray, mat4) {
        orientationArray.forEach((orientation) => {
            mat4.multiplyMatrices(Orientation.toMatrix(orientation), mat4);
        });
    }
    /**
     * sets a single matrix to the object (in such a way the object.updateMatrix stuff will work correctly)
     * @param object3D
     * @param mat4
     */
    static setMatrix(object3D, mat4) {
        const pos = new Vector3();
        const rot = new Quaternion();
        const scale = new Vector3(1, 1, 1);
        mat4.decompose(pos, rot, scale);
        object3D.position.copy(pos);
        object3D.quaternion.copy(rot);
        object3D.scale.copy(scale);
    }
    /**
     * transforms an orientation setting to a matrix
     * RULE : Matrix before compose and quaternion before rotation (Euler)
     * @param orientation
     */
    static toMatrix(orientation) {
        const mat4 = new Matrix4();
        if (orientation.matrix) {
            mat4.fromArray(orientation.matrix);
        }
        else {
            const pos = new Vector3();
            const rot = new Quaternion();
            const scale = new Vector3(1, 1, 1);
            this.fromSettings(orientation, pos, rot, scale);
            mat4.compose(pos, rot, scale);
        }
        return mat4;
    }
    /**
     * transforms an orientation setting to the components position, rotation, scale
     * @param orientation
     * @param pos
     * @param rot
     * @param scale
     */
    static fromSettings(orientation, pos, rot, scale) {
        if (orientation.position) {
            pos.fromArray(orientation.position);
        }
        if (orientation.quaternion) {
            rot.fromArray(orientation.quaternion);
        }
        else if (orientation.rotation) {
            const euler = new Euler();
            euler.fromArray(orientation.rotation);
            rot.setFromEuler(euler);
        }
        if (orientation.scale) {
            scale.fromArray(orientation.scale);
        }
    }
    /**
     * transforms a matrix to Orientation settings
     * @param mat4
     */
    static fromMatrixToSettings(mat4) {
        const pos = new Vector3();
        const rot = new Quaternion();
        const scale = new Vector3(1, 1, 1);
        mat4.decompose(pos, rot, scale);
        return {
            position: pos.toArray(),
            scale: scale.toArray(),
            quaternion: rot.toArray(),
        };
    }
    /**
     * sets a single orientation setting to an object
     * @param object3D
     * @param orientation
     */
    static setOrientation(object3D, orientation) {
        const pos = new Vector3();
        const rot = new Quaternion();
        const scale = new Vector3(1, 1, 1);
        if (orientation.matrix) {
            object3D.matrix.fromArray(orientation.matrix);
            object3D.matrix.decompose(pos, rot, scale);
        }
        else {
            this.fromSettings(orientation, pos, rot, scale);
        }
        object3D.position.copy(pos);
        object3D.quaternion.copy(rot);
        object3D.scale.copy(scale);
    }
}
//# sourceMappingURL=Orientation.js.map